/**
 * ConstructionDataUpload Component
 * 
 * Component for handling file uploads of construction worker salary data.
 * Provides a comprehensive interface for uploading construction worker data files
 * with validation, preview, and progress tracking.
 * 
 * Features:
 * - Drag and drop file upload interface
 * - File type toggle: 給与 (construction salary) vs 賞与 (construction bonus)
 * - Auto-detects ファイルタイプ from filename when files are chosen
 * - File type and size validation (TXT/CSV files, 10MB max)
 * - Re-upload replaces existing data for the same site/year/month (salary) or site/year/period (bonus)
 * - Site name (現場/genba), year, and month/period extraction from filename
 * - Upload progress tracking (percentage display)
 * - UTF-8 encoding conversion for Japanese characters
 * - Error handling and user feedback
 * - Japanese language interface
 * 
 * Note: This component is for construction workers only.
 * Office employees use DataUpload component.
 * 
 * Salary filename: {現場名}_{YYYY}_{MM}.txt
 * Bonus filename: 賞与_{現場名}_{YYYY}_{MM}.txt
 * 
 * Used by:
 * - SalaryDataMain component for construction worker data upload
 * 
 * @component
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import '../../css/DataUpload.css';
import { API_BASE_URL } from '../../../../config/apiConfig';
import { authenticatedFetch } from '../../../../utils/authenticatedFetch';
import { fileParserUtils } from '../../../../utils/FileParserUtils';
import { normalizeGenbaName } from '../../../../utils/genbaNameMatch';
import {
  CONSTRUCTION_BONUS_API_BASE_PATH,
  parseConstructionBonusCsv,
  parseConstructionBonusFilename,
} from '../../../../utils/constructionBonusContract';
import {
  extractConstructionSalaryEmployeeNames,
  findUnregisteredUploadedNames,
  formatUnregisteredNamesAlert,
} from '../../../../utils/constructionUploadNameCheck';
import ConstructionWorkerService from '../../../../services/ConstructionWorkerService';

/**
 * Uploaded File Interface for Construction Workers
 * 
 * Represents a file that has been selected for upload with its current status
 * and processing information. This interface tracks the complete lifecycle
 * of file uploads from selection to completion.
 * 
 * @interface ConstructionUploadedFile
 */
interface ConstructionUploadedFile {
  /** Unique identifier for the file */
  id: string;
  /** Original filename */
  name: string;
  /** File size in bytes */
  size: number;
  /** MIME type of the file */
  type: string;
  /** Current processing status */
  status: 'uploading' | 'success' | 'error' | 'pending' | 'parsing';
  /** Upload progress percentage (0-100) */
  progress: number;
  /** Error message if upload failed */
  error?: string;
  /** Parsed file content for preview */
  parsedData?: any;
  /** Reference to the original File object */
  originalFile: File;
  /** Site name (現場名) from filename - for multi-file upload */
  genba?: string;
  /** Year from filename */
  year?: number;
  /** Month from filename (salary) */
  month?: number;
  /** Canonical bonus period "1".."12" */
  bonusPeriod?: string;
  fileKind?: 'salary' | 'bonus';
}

/**
 * ConstructionDataUpload Component
 * 
 * Main component for uploading construction worker salary CSV/TXT files.
 * Manages file selection, validation, parsing, preview, and upload operations.
 * 
 * @returns React functional component
 */
export const ConstructionDataUpload = () => {
  /**
   * State management:
   * - uploadedFiles: Array of uploaded files with their status and progress
   * - isDragOver: Drag and drop state indicator
   * - isUploading: Global upload state indicator
   * - fileInputRef: Reference to hidden file input element
   */
  const [uploadedFiles, setUploadedFiles] = useState<ConstructionUploadedFile[]>([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [fileType, setFileType] = useState<'salary' | 'bonus'>('salary');
  const [nameRegistrationAlerts, setNameRegistrationAlerts] = useState<string[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const registeredAccountNamesRef = useRef<string[] | null>(null);

  const loadRegisteredAccountNames = useCallback(async (): Promise<string[]> => {
    if (registeredAccountNamesRef.current) {
      return registeredAccountNamesRef.current;
    }
    try {
      const result = await ConstructionWorkerService.getAllConstructionWorkers();
      const names =
        result.success && result.data
          ? result.data.map((w) => w.name).filter((n): n is string => Boolean(n?.trim()))
          : [];
      registeredAccountNamesRef.current = names;
      return names;
    } catch {
      registeredAccountNamesRef.current = [];
      return [];
    }
  }, []);

  const refreshNameRegistrationAlerts = useCallback(
    async (files: ConstructionUploadedFile[]) => {
      const uploadedNames = files.flatMap((f) =>
        Array.isArray(f.parsedData?.employeeNames) ? (f.parsedData.employeeNames as string[]) : []
      );
      if (uploadedNames.length === 0) {
        setNameRegistrationAlerts([]);
        return;
      }
      const registered = await loadRegisteredAccountNames();
      setNameRegistrationAlerts(findUnregisteredUploadedNames(uploadedNames, registered));
    },
    [loadRegisteredAccountNames]
  );

  /** Clamp carousel index when file list changes */
  useEffect(() => {
    if (uploadedFiles.length === 0) {
      setCurrentFileIndex(0);
    } else if (currentFileIndex >= uploadedFiles.length) {
      setCurrentFileIndex(uploadedFiles.length - 1);
    }
  }, [uploadedFiles.length, currentFileIndex]);

  /**
   * Allowed file types for construction worker data (MIME types).
   * Supports both TXT and CSV formats for construction worker salary data.
   * 
   * @constant
   */
  const allowedFileTypes = [
    'text/plain',
    'text/csv',
    'application/csv',
    'text/comma-separated-values'
  ];

  /**
   * Allowed file extensions as fallback validation.
   * Ensures compatibility with various file type detection methods.
   * Used when MIME type detection fails.
   * 
   * @constant
   */
  const allowedExtensions = ['.txt', '.csv'];

  /**
   * Extracts site name (現場/genba), year, and month from filename.
   * 
   * Expected format: "{現場名}_{YYYY}_{MM}.txt" or "{現場名}_{YYYY}_{MM} 1.txt"
   * 
   * Filename is split by underscores (_):
   * - Part 0: Site name (現場名/genba)
   * - Part 1: Year (4-digit, e.g., 2024)
   * - Part 2: Month (1-12, may have trailing numbers that are ignored)
   * 
   * @param filename - The filename to extract information from
   * @returns Object with genba, year, and month if found, null otherwise
   * 
   * @example
   * ```typescript
   * extractFileInfo("現場A_2024_7.txt");
   * // Returns: { genba: "現場A", year: 2024, month: 7 }
   * 
   * extractFileInfo("現場A_2024_7 1.txt");
   * // Returns: { genba: "現場A", year: 2024, month: 7 } (trailing "1" ignored)
   * 
   * extractFileInfo("invalid.txt");
   * // Returns: null (doesn't match expected format)
   * ```
   */
  const extractSalaryFileInfo = (filename: string) => {
    const nameWithoutExt = filename.replace(/\.(txt|csv)$/i, '');
    const parts = nameWithoutExt.split('_');

    if (parts.length >= 3 && parts[0] !== '賞与') {
      const genba = normalizeGenbaName(parts[0]);
      const year = parseInt(parts[1], 10);
      const monthPart = parts[2].split(' ')[0];
      const month = parseInt(monthPart, 10);

      if (!isNaN(year) && !isNaN(month) && year > 2000 && year < 2100 && month >= 1 && month <= 12) {
        return { genba, year, month };
      }
    }

    return null;
  };

  /** Detect 給与 vs 賞与 from filename (賞与_現場_YYYY_MM vs 現場_YYYY_MM). */
  const detectConstructionFileKind = (filename: string): 'salary' | 'bonus' | null => {
    if (parseConstructionBonusFilename(filename)) {
      return 'bonus';
    }
    if (extractSalaryFileInfo(filename)) {
      return 'salary';
    }
    return null;
  };

  const handleFileTypeChange = (nextType: 'salary' | 'bonus') => {
    if (nextType === fileType) {
      return;
    }
    if (uploadedFiles.length > 0) {
      if (!window.confirm('ファイルタイプを切り替えると選択中のファイルがクリアされます。よろしいですか？')) {
        return;
      }
    }
    setFileType(nextType);
    setUploadedFiles([]);
    setCurrentFileIndex(0);
    setNameRegistrationAlerts([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Validates a file before processing.
   * 
   * Performs comprehensive file validation:
   * - File type checking (MIME type and extension)
   * - File size validation (10MB limit, 100 byte minimum)
   * - Ensures file contains actual data
   * 
   * @param file - File object to validate
   * @returns Error message if validation fails, null if valid
   * 
   * @example
   * ```typescript
   * const error = validateFile(file);
   * if (error) {
   *   console.error(error); // "Unsupported file type..." or "File size too large..."
   * } else {
   *   // File is valid, proceed with processing
   * }
   * ```
   */
  const validateFile = (file: File): string | null => {
    // Check file type against allowed MIME types and extensions
    if (!allowedFileTypes.includes(file.type) && 
        !allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext))) {
      return 'Unsupported file type. Please upload TXT or CSV files only.';
    }

    // Check file size (10MB limit to prevent system overload)
    if (file.size > 10 * 1024 * 1024) {
      return 'File size too large. Maximum size is 10MB.';
    }

    // Check minimum file size (at least 100 bytes to ensure it's not just a filename)
    if (file.size < 100) {
      return 'File is too small. Please ensure the file contains actual data.';
    }

    return null;
  };

  /**
   * Handles the selection of files from the file input or drag and drop (supports multiple files).
   * Auto-detects ファイルタイプ from filename (賞与_… → 賞与, 現場_YYYY_MM → 給与), like office upload.
   */
  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);

    const detectedKinds = fileArray
      .map((file) => detectConstructionFileKind(file.name))
      .filter((kind): kind is 'salary' | 'bonus' => kind != null);

    let activeType: 'salary' | 'bonus' = fileType;
    if (detectedKinds.length > 0) {
      const uniqueKinds = Array.from(new Set(detectedKinds));
      if (uniqueKinds.length > 1) {
        alert(
          '給与ファイルと賞与ファイルが混在しています。同じタイプのファイルのみを選択してください。'
        );
        return;
      }
      activeType = uniqueKinds[0];
      if (activeType !== fileType) {
        setFileType(activeType);
      }
    }

    const filenameError =
      activeType === 'bonus'
        ? 'ファイル名から現場名、年、賞与期間を抽出できませんでした。ファイル名の形式を確認してください。（賞与_現場名_YYYY_MM.txt）'
        : 'ファイル名から現場名、年、月の情報を抽出できませんでした。ファイル名の形式を確認してください。（現場名_YYYY_MM.txt）';

    const newFiles: ConstructionUploadedFile[] = fileArray.map((file, idx) => {
      const basicError = validateFile(file);
      const id = Date.now() + idx + Math.random().toString(36).substr(2, 9);
      if (basicError) {
        return {
          id,
          name: file.name,
          size: file.size,
          type: file.type,
          status: 'error' as const,
          progress: 0,
          error: basicError,
          originalFile: file,
          fileKind: activeType,
        };
      }

      if (activeType === 'bonus') {
        const bonusInfo = parseConstructionBonusFilename(file.name);
        if (!bonusInfo) {
          return {
            id,
            name: file.name,
            size: file.size,
            type: file.type,
            status: 'error' as const,
            progress: 0,
            error: filenameError,
            originalFile: file,
            fileKind: 'bonus',
          };
        }
        return {
          id,
          name: file.name,
          size: file.size,
          type: file.type,
          status: 'parsing' as const,
          progress: 0,
          originalFile: file,
          genba: bonusInfo.genbaName,
          year: bonusInfo.year,
          month: parseInt(bonusInfo.bonusPeriod, 10),
          bonusPeriod: bonusInfo.bonusPeriod,
          fileKind: 'bonus',
        };
      }

      const fileInfo = extractSalaryFileInfo(file.name);
      if (!fileInfo) {
        return {
          id,
          name: file.name,
          size: file.size,
          type: file.type,
          status: 'error' as const,
          progress: 0,
          error: filenameError,
          originalFile: file,
          fileKind: 'salary',
        };
      }
      return {
        id,
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'parsing' as const,
        progress: 0,
        originalFile: file,
        genba: fileInfo.genba,
        year: fileInfo.year,
        month: fileInfo.month,
        fileKind: 'salary',
      };
    });

    setUploadedFiles(newFiles);
    setCurrentFileIndex(0);
    setNameRegistrationAlerts([]);

    let latestFiles = newFiles;
    for (const nf of newFiles) {
      if (nf.status !== 'parsing') {
        continue;
      }
      if (nf.fileKind === 'bonus') {
        try {
          const utf8File = await fileParserUtils.convertFileToUTF8(nf.originalFile);
          const content = await utf8File.text();
          const rows = parseConstructionBonusCsv(content);
          if (!rows.length) {
            throw new Error('賞与データが空です。');
          }
          const employeeNames = rows.map((r) => r.employeeName);
          latestFiles = latestFiles.map((f) =>
            f.id === nf.id
              ? {
                  ...f,
                  status: 'pending' as const,
                  parsedData: { success: true, fileName: f.name, employeeNames },
                }
              : f
          );
          setUploadedFiles(latestFiles);
        } catch (error: any) {
          latestFiles = latestFiles.map((f) =>
            f.id === nf.id
              ? {
                  ...f,
                  status: 'error' as const,
                  error: error?.message || '賞与ファイルの解析に失敗しました。',
                }
              : f
          );
          setUploadedFiles(latestFiles);
        }
      } else {
        try {
          const utf8File = await fileParserUtils.convertFileToUTF8(nf.originalFile);
          const content = await utf8File.text();
          const employeeNames = extractConstructionSalaryEmployeeNames(content);
          latestFiles = latestFiles.map((f) =>
            f.id === nf.id
              ? {
                  ...f,
                  status: 'pending' as const,
                  parsedData: { success: true, fileName: f.name, employeeNames },
                }
              : f
          );
          setUploadedFiles(latestFiles);
        } catch (error: any) {
          latestFiles = latestFiles.map((f) =>
            f.id === nf.id
              ? {
                  ...f,
                  status: 'error' as const,
                  error: error?.message || '給与ファイルの解析に失敗しました。',
                }
              : f
          );
          setUploadedFiles(latestFiles);
        }
      }
    }
    await refreshNameRegistrationAlerts(latestFiles);
  }, [fileType, refreshNameRegistrationAlerts]);

  /**
   * Handles drag over events for the upload area
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  /**
   * Handles drag leave events for the upload area
   */
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  /**
   * Handles file drop events for the upload area
   */
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  /**
   * Handles file input change events
   */
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Performs the actual file upload to the backend (uses file's own genba/year/month).
   */
  const performUpload = async (fileId: string, fileList: ConstructionUploadedFile[]): Promise<boolean> => {
    const file = fileList.find(f => f.id === fileId);
    if (!file || !file.parsedData) return false;

    const genba = file.genba;
    const year = file.year;
    const month = file.month;
    const bonusPeriod = file.bonusPeriod;
    const kind = file.fileKind ?? 'salary';
    if (kind === 'bonus') {
      if (genba == null || year == null || bonusPeriod == null) {
        alert('ファイルから現場名、年、賞与期間の情報を取得できませんでした。ファイルを確認してください。');
        return false;
      }
    } else if (genba == null || year == null || month == null) {
      alert('ファイルから現場名、年、月の情報を取得できませんでした。ファイルを確認してください。');
      return false;
    }

    setIsUploading(true);
    setUploadedFiles(prev =>
      prev.map(f => (f.id === fileId ? { ...f, status: 'uploading' as const } : f))
    );

    try {
      const utf8File = await fileParserUtils.convertFileToUTF8(file.originalFile);
      const formData = new FormData();
      formData.append('file', utf8File);
      formData.append('genbaName', genba);
      formData.append('year', year.toString());
      const uploadPath = kind === 'bonus'
        ? `${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}/upload`
        : `${API_BASE_URL}/construction-salary/upload`;
      if (kind === 'bonus') {
        formData.append('period', bonusPeriod as string);
      } else {
        formData.append('month', (month as number).toString());
      }

      const progressInterval = setInterval(() => {
        setUploadedFiles(prev =>
          prev.map(f =>
            f.id === fileId ? { ...f, progress: Math.min(f.progress + 10, 90) } : f
          )
        );
      }, 200);

      const response = await authenticatedFetch(uploadPath, {
        method: 'POST',
        body: formData
      });
      clearInterval(progressInterval);
      const result = await response.json();

      if (result.success) {
        setUploadedFiles(prev =>
          prev.map(f => (f.id === fileId ? { ...f, status: 'success' as const, progress: 100 } : f))
        );
        return true;
      } else {
        throw new Error(result.message || 'アップロードに失敗しました');
      }
    } catch (error: any) {
      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === fileId
            ? { ...f, status: 'error' as const, error: error.message || 'アップロードに失敗しました' }
            : f
        )
      );
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Handles the main upload button click (upload all pending files).
   */
  const handleUpload = async () => {
    if (uploadedFiles.length === 0) {
      alert('ファイルを選択してください');
      return;
    }

    const pendingFiles = uploadedFiles.filter((f) => {
      if (f.status !== 'pending' || f.genba == null || f.year == null) {
        return false;
      }
      return f.fileKind === 'bonus' ? f.bonusPeriod != null : f.month != null;
    });
    if (pendingFiles.length === 0) {
      alert('アップロード可能なファイルがありません。ファイルの準備を確認してください。');
      return;
    }

    if (!window.confirm(`${pendingFiles.length}件のファイルをアップロードしますか？`)) return;

    let successCount = 0;
    for (let i = 0; i < pendingFiles.length; i++) {
      const file = pendingFiles[i];
      if (await performUpload(file.id, uploadedFiles)) {
        successCount += 1;
      }
    }
    const kindLabel = fileType === 'bonus' ? '賞与' : '給与';
    if (successCount === pendingFiles.length) {
      alert(`${successCount}件の建設作業員${kindLabel}データが正常にアップロードされました。`);
    } else if (successCount > 0) {
      alert(`${successCount}件の建設作業員${kindLabel}データがアップロードされました。${pendingFiles.length - successCount}件は失敗しました。`);
    } else {
      alert('アップロードに失敗しました。ファイルとエラー内容を確認してください。');
    }
  };

  /**
   * Handles the cancel button click
   */
  const handleCancel = () => {
    if (uploadedFiles.length > 0) {
      if (window.confirm('選択したファイルと設定をリセットしてもよろしいですか？')) {
        setUploadedFiles([]);
        setCurrentFileIndex(0);
        setNameRegistrationAlerts([]);
      }
    }
  };

  /**
   * Removes a file from the uploaded files list and adjust carousel index
   */
  const removeFile = (fileId: string) => {
    setUploadedFiles((prev) => {
      const next = prev.filter((f) => f.id !== fileId);
      setCurrentFileIndex((i) => (i >= next.length ? Math.max(0, next.length - 1) : i));
      void refreshNameRegistrationAlerts(next);
      return next;
    });
  };

  /**
   * Retries the upload for a specific file
   */
  const retryUpload = (fileId: string) => {
    setUploadedFiles(prev =>
      prev.map(f =>
        f.id === fileId ? { ...f, status: 'pending' as const, progress: 0, error: undefined } : f
      )
    );
    performUpload(fileId, uploadedFiles);
  };

  /**
   * Formats a file size in bytes to a human-readable string
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  /**
   * Returns an icon based on the file status
   */
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'uploading':
        return '⏳';
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'parsing':
        return '🔍';
      default:
        return '📄';
    }
  };

  const currentFile = uploadedFiles.length > 0
    ? uploadedFiles[Math.min(currentFileIndex, uploadedFiles.length - 1)]
    : undefined;
  const detectedPlaceholder = uploadedFiles.length > 0 ? '—' : 'ファイルから自動検出中...';
  const periodValue = fileType === 'bonus'
    ? (currentFile?.bonusPeriod != null ? `${currentFile.bonusPeriod}月` : detectedPlaceholder)
    : (currentFile?.month != null ? `${currentFile.month}月` : detectedPlaceholder);

  return (
    <div className="data-upload-container">
      <div className="construction-upload-stack">
      <div className="upload-layout">
        {/* Left Panel - ファイルタイプ / 現場名 / 年 / 月 or 賞与期間 */}
        <div className="left-panel">
          <div className="selectors-section construction-carousel-section">
            <div className="carousel-fields">
              <div className="selector-group">
                <label htmlFor="construction-file-type-select">ファイルタイプ:</label>
                <select
                  id="construction-file-type-select"
                  value={fileType}
                  onChange={(e) => handleFileTypeChange(e.target.value as 'salary' | 'bonus')}
                  className="file-type-selector"
                  disabled={isUploading}
                >
                  <option value="salary">給与データ</option>
                  <option value="bonus">賞与データ</option>
                </select>
              </div>
              <div className="selector-group">
                <label htmlFor="genba-display">現場名:</label>
                <input
                  id="genba-display"
                  type="text"
                  value={currentFile ? (currentFile.genba ?? '—') : 'ファイルから自動検出中...'}
                  readOnly
                  className="genba-display"
                />
              </div>
              <div className="selector-group">
                <label htmlFor="year-display">年:</label>
                <input
                  id="year-display"
                  type="text"
                  value={currentFile?.year != null ? `${currentFile.year}年` : detectedPlaceholder}
                  readOnly
                  className="year-display"
                />
              </div>
              {fileType === 'salary' ? (
                <div className="selector-group">
                  <label htmlFor="month-display">月:</label>
                  <input
                    id="month-display"
                    type="text"
                    value={periodValue}
                    readOnly
                    className="month-display"
                  />
                </div>
              ) : (
                <div className="selector-group">
                  <label htmlFor="bonus-period-display">賞与期間:</label>
                  <input
                    id="bonus-period-display"
                    type="text"
                    value={periodValue}
                    readOnly
                    className="bonus-period-display"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="buttons-section">
            <button
              className="upload-btn"
              onClick={handleUpload}
              disabled={
                uploadedFiles.length === 0
                || isUploading
                || uploadedFiles.some((f) => f.status === 'parsing')
                || !uploadedFiles.some((f) => f.status === 'pending')
              }
            >
              {isUploading ? 'アップロード中...' : (uploadedFiles.length > 1 ? '一括アップロード' : 'アップロード')}
            </button>
            <button className="cancel-btn" onClick={handleCancel} disabled={isUploading}>
              キャンセル
            </button>
          </div>
        </div>

        {/* Right Panel - File Upload Interface */}
        <div className="right-panel">
          {/* File Upload Section */}
          <div className="upload-section">
            {/* Drag and Drop Upload Area */}
            <div 
              className={`upload-area ${isDragOver ? 'drag-over' : ''} ${uploadedFiles.length > 0 ? 'has-file' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => uploadedFiles.length === 0 && fileInputRef.current?.click()}
            >
              {uploadedFiles.length === 0 ? (
                <div className="upload-content">
                  <div className="upload-icon">📁</div>
                  <h4>
                    {fileType === 'bonus'
                      ? '建設作業員賞与データをここにアップロードしてください'
                      : '建設作業員給与データをここにアップロードしてください'}
                  </h4>
                  <p>ファイルをドラッグ＆ドロップまたはクリックして選択（複数可）</p>
                  <div className="supported-formats">
                    <span>対応形式: TXT、CSVファイル</span>
                    <span>最大サイズ: 10MB</span>
                    <span>
                      {fileType === 'bonus'
                        ? 'ファイル名形式: 賞与_現場名_YYYY_MM.txt'
                        : 'ファイル名形式: 現場名_YYYY_MM.txt'}
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="file-display file-carousel">
                    {uploadedFiles.length > 1 && (
                      <button
                        type="button"
                        className="carousel-arrow carousel-arrow-left"
                        onClick={(e) => { e.stopPropagation(); setCurrentFileIndex(i => Math.max(0, i - 1)); }}
                        disabled={currentFileIndex === 0}
                        aria-label="前のファイル"
                      >
                        ◀
                      </button>
                    )}
                    <div className="file-display-inner">
                      {(() => {
                        const idx = Math.min(currentFileIndex, uploadedFiles.length - 1);
                        const file = uploadedFiles[idx];
                        return (
                          <>
                            <div className="file-info">
                              <div className="file-icon">{getStatusIcon(file.status)}</div>
                              <div className="file-details">
                                <div className="file-name">{file.name}</div>
                                <div className="file-meta">
                                  {formatFileSize(file.size)} • {file.type || 'text/plain'}
                                </div>
                                {file.error && <div className="file-error">{file.error}</div>}
                              </div>
                            </div>
                            <div className="file-actions">
                              {file.status === 'uploading' && (
                                <div className="progress-bar">
                                  <div className="progress-fill" style={{ width: `${file.progress}%` }} />
                                  <span className="progress-text">{Math.round(file.progress)}%</span>
                                </div>
                              )}
                              {file.status === 'success' && (
                                <div className="success-message"><span>アップロード完了</span></div>
                              )}
                              {file.status === 'error' && (
                                <button className="retry-button" onClick={(e) => { e.stopPropagation(); retryUpload(file.id); }}>
                                  再試行
                                </button>
                              )}
                              {file.status === 'parsing' && (
                                <div className="parsing-message"><span>ファイル解析中...</span></div>
                              )}
                              <button
                                className="remove-button"
                                onClick={(e) => { e.stopPropagation(); removeFile(file.id); }}
                                title="ファイルを削除"
                              >
                                🗑️
                              </button>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    {uploadedFiles.length > 1 && (
                      <button
                        type="button"
                        className="carousel-arrow carousel-arrow-right"
                        onClick={(e) => { e.stopPropagation(); setCurrentFileIndex(i => Math.min(uploadedFiles.length - 1, i + 1)); }}
                        disabled={currentFileIndex === uploadedFiles.length - 1}
                        aria-label="次のファイル"
                      >
                        ▶
                      </button>
                    )}
                  </div>
                  <div className="upload-area-footer">
                    <p className="multi-file-message">
                      {uploadedFiles.length}件のファイルがアップロードの準備ができています。アップロード前に各ファイルの詳細を確認してください。
                    </p>
                    {uploadedFiles.length > 1 && (
                      <p className="carousel-index">
                        ファイル {Math.min(currentFileIndex, uploadedFiles.length - 1) + 1} / {uploadedFiles.length}
                      </p>
                    )}
                  </div>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.csv"
                multiple
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
            </div>

            {nameRegistrationAlerts.length > 0 && (
              <div className="data-exists-warning" role="status">
                <div className="warning-icon">⚠️</div>
                <div className="warning-message">
                  <strong>登録アカウントとの氏名確認</strong>
                  <p style={{ margin: '8px 0 0' }}>
                    {formatUnregisteredNamesAlert(nameRegistrationAlerts)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      </div>

      {/* Global Upload Status Indicator */}
      {isUploading && (
        <div className="upload-status">
          <div className="status-indicator">
            <div className="spinner"></div>
            <span>アップロード中...</span>
          </div>
        </div>
      )}

    </div>
  );
};
